import { LitElement } from 'lit'

import './not-found.js'

import routes from './routes.js'
import AppEvents from '../../src/utilities/app-events.js'
import {
  getSessionGlobals,
  getSessionUser,
} from '../../src/utilities/session.js'
import {
  appendParamsObjToPath,
  decodeQuerystringValues,
  getRouteByPath,
} from './router.util.js'

const APP_NAME = process.env.APP_NAME

/**
 * @param {string} path
 * @param {SupportedParams} params
 * */
export function go(path, params = null) {
  if (getRouteByPath(path, routes)) {
    const finalUrl = params ? appendParamsObjToPath(path, params) : path
    window.history.pushState(null, null, finalUrl)
    window.dispatchEvent(new CustomEvent(AppEvents.ROUTE_CHANGE))
  }
}

/**
 * @param {Object} params
 */
export function updateUrlParams(params) {
  const url = new URL(window.location.href)
  Object.entries(params).forEach(([key, value]) =>
    url.searchParams.set(key, value)
  )
  window.history.pushState(null, null, url)
}

/**
 * @param {Array<string>} params
 */
export function removeUrlParams(params) {
  const url = new URL(window.location.href)
  params.forEach((p) => url.searchParams.delete(p))
  window.history.pushState(null, null, url)
}

const enterpriseComponentLoader = (/** @type {string} */ pathname) =>
  import(`./../../enterprise/views${pathname}.js`)
const clientComponentLoader = (/** @type {string} */ pathname) =>
  import(`./../../src/ui/views${pathname}.js`)

export default class RouterMixin extends LitElement {
  static properties = {
    current: { type: Object },
    showNav: { type: Boolean },
    hasToolbar: { type: Boolean },
    showHeader: { type: Boolean },
    showBackArrow: { type: Boolean },
    hideWalletSelect: { type: Boolean },
    showHeaderQuickNav: { type: Boolean },
    routeHistory: { type: Array },
  }

  constructor() {
    super()

    /** @type {string} */
    this.app = APP_NAME
    /** @type {RouteEnterArgs} */
    this.current = null
    /** @type {Boolean} Route property (showMobileNav). If true, displays mobile nav bar */
    this.showMobileNav = null
    /** @type {Boolean} Route property (showNav). If true, displays header bar for the app */
    this.showHeader = null
    /** @type {function} */
    this.getRouteByPath = getRouteByPath
    /** @type {function} */
    this.decodeQuerystringValues = decodeQuerystringValues
    /** @type {boolean} */
    this.hideWalletSelect = null
    /** @type {boolean} */
    this.hasToolbar = null
    /** @type {Array<Route>} */
    this.routeHistory = []
    /** @type {RouteEnterArgs} */
    // Handle direct url routing (including initial page load)
    window.addEventListener(AppEvents.PAGE_LOAD, () => this.urlChange(), false)
    // Handle back and forward button events
    window.addEventListener(AppEvents.POPSTATE, () => this.urlChange(), false)
    window.addEventListener('back', () => this.goBack(), false)
  }

  connectedCallback() {
    super.connectedCallback()

    window.addEventListener(
      AppEvents.ROUTE_CHANGE,
      () => this.urlChange(),
      false
    )
  }

  /**
   * @param {Route} routeObj
   * @returns {Promise<Route | null>}
   * */
  async checkRoutePermissions(routeObj) {
    if (window.Globals?.initializationPromise) {
      await window.Globals.initializationPromise
    }

    /** @type {PermissionParams} */
    const options = {
      sessionUser: getSessionUser(),
      globals: getSessionGlobals(),
      kipRoles: window.Globals?.kipRoles,
    }

    let redirect = null

    for (const permission of routeObj.permissions) {
      redirect = permission(options)
      if (redirect) break
    }

    return redirect
  }

  /**
   * @param {Route} routeObj
   * @param {RouteContext} context
   * */
  setRouteProperties(routeObj, context) {
    Object.assign(this, {
      showNav: routeObj.showNav && !!getSessionUser()?.id,
      showMobileNav: routeObj.showMobileNav,
      hideWalletSelect: routeObj.hideWalletSelect,
      hasToolbar: routeObj.hasToolbar,
      showHeader: routeObj.showHeader == true,
      showBackArrow: routeObj.showBackArrow == true,
      showHeaderQuickNav: routeObj.showHeaderQuickNav,
      current: { nextView: routeObj, context },
    })
  }

  async urlChange() {
    // TODO: change default route after dev
    if (window.location.pathname === '/') {
      const queryParams = new URLSearchParams(window.location.search)
      const params = Array.from(queryParams.entries()).reduce(
        (acc, [key, value]) => {
          acc[key] = value
          return acc
        },
        {}
      )
      return go(routes.LOGIN.path, params)
    }

    const routeObj = this.getRouteByPath(window.location.pathname, routes)
    if (!routeObj) return this.updateSlot(document.createElement('not-found'))

    const redirectRoute = await this.checkRoutePermissions(routeObj)
    if (redirectRoute) {
      return go(redirectRoute.path)
    }

    /**
     * @description converts querystring to object (e.g. ?name=Sue&age=43 -> { name: 'Sue', age: 43 })
     * @type {RouteContext}
     * */
    const context = {
      params: this.decodeQuerystringValues(window.location.search),
    }

    if (window.location.pathname !== '/') {
      this.routeHistory.push(routeObj)
      if (this.routeHistory.length > 20) {
        this.routeHistory = this.routeHistory.slice(-20)
      }
    }

    try {
      /** Load component file, which registers the custom elemnent with the DOM */
      this.app == 'enterprise'
        ? await enterpriseComponentLoader(routeObj.componentPath)
        : await clientComponentLoader(routeObj.componentPath)
    } catch (error) {
      console.error(error)
      return this.updateSlot(document.createElement('not-found'))
    }

    this.setRouteProperties(routeObj, context)
    this.updateUI(routeObj, context)
  }

  goBack() {
    if (this.routeHistory.length > 1) {
      this.routeHistory.pop()
      const previousRoute = this.routeHistory[this.routeHistory.length - 1]
      this.routeHistory.pop()
      go(previousRoute.path)
    } else {
      go(routes.DASHBOARD.path)
    }
  }

  /**
   * @param {Route} nextView
   * @param {RouteContext} context
   * */
  async updateUI(nextView, context) {
    const component = /** @type {RouteContainerComponent} */ (
      document.createElement(nextView.componentName)
    )

    if (component.onGlobalsUpdate) {
      // @ts-ignore
      component.disconnectGlobals = window.Globals.onUpdate(
        component?.onGlobalsUpdate?.bind(component)
      )
    }

    // If component has a route enter, call it before appending it to the DOM
    component.routeEnter && (await component.routeEnter({ nextView, context }))

    this.updateSlot(component)
  }

  /** @param {RouteContainerComponent} component */
  updateSlot(component) {
    const slot = this.shadowRoot.querySelector('slot')
    const currentComponent = /** @type {RouteContainerComponent} */ (
      slot.assignedElements()[0]
    )

    currentComponent?.disconnectGlobals && currentComponent.disconnectGlobals()

    slot.innerHTML = ''
    slot.append(component)
  }
}

/**
 * Chrome fires popstate on load, unlike Firefox and Safari which do not
 * This prevents that behavior and makes Chrome function like FF and S
 */
;(function () {
  let blockPopstateEvent = document.readyState != 'complete'
  window.addEventListener(
    'load',
    () =>
      // The timeout ensures that popstate-events will be unblocked right
      // after the load event occured, but not in the same event-loop cycle.
      setTimeout(function () {
        blockPopstateEvent = false
      }, 0),
    false
  )
  window.addEventListener(
    'popstate',
    (evt) => {
      if (blockPopstateEvent && document.readyState == 'complete') {
        evt.preventDefault()
        evt.stopImmediatePropagation()
      }
    },
    false
  )
})()
